import {
  dictionariesId
} from "./chunk-Z7QXQM57.js";
import {
  __publicField,
  __spreadProps,
  __spreadValues
} from "./chunk-IZNJOSL5.js";

// src/getComponentTranslations.ts
async function getComponentTranslations(locale, componentKey) {
  try {
    return import(
      /* webpackInclude: /[\\/]i18n[\\/](.+)\.json$/ */
      `@volvo-cars/${componentKey}/i18n/${locale}.json`
    );
  } catch (error) {
    console.log(error);
    return { default: {} };
  }
}

// src/Translator.ts
var Translator = class {
  constructor() {
    __publicField(this, "cache");
    __publicField(this, "currentLocale");
    __publicField(this, "changeListeners");
    __publicField(this, "updateCache", (componentKey, value, options) => {
      var _a;
      const locale = this.currentLocale;
      if (!locale)
        return;
      const cacheByLocale = (_a = this.cache) == null ? void 0 : _a[locale];
      if (cacheByLocale) {
        this.cache = __spreadProps(__spreadValues({}, this.cache), {
          [locale]: __spreadProps(__spreadValues({}, cacheByLocale), {
            [componentKey]: value
          })
        });
      } else {
        this.cache = __spreadProps(__spreadValues({}, this.cache), {
          [locale]: { [componentKey]: value }
        });
      }
      if (!(options == null ? void 0 : options.skipEmit)) {
        this.emitChange();
      }
    });
    this.cache = this.getTranslations();
    this.setLocale = this.setLocale.bind(this);
    this.translate = this.translate.bind(this);
    this.updateCache = this.updateCache.bind(this);
    this.subscribe = this.subscribe.bind(this);
    this.unsubscribe = this.unsubscribe.bind(this);
    this.emitChange = this.emitChange.bind(this);
    this.changeListeners = [];
  }
  emitChange() {
    this.changeListeners.forEach((listener) => listener(this));
  }
  subscribe(listener, options) {
    if ((options == null ? void 0 : options.once) && this.changeListeners.find((cachedListener) => listener === cachedListener)) {
      return;
    }
    this.changeListeners.push(listener);
  }
  unsubscribe(listener) {
    this.changeListeners = this.changeListeners.filter((cachedListener) => listener !== cachedListener);
  }
  setLocale(locale, options) {
    if (this.currentLocale !== locale || !this.currentLocale) {
      this.currentLocale = locale;
      if (!(options == null ? void 0 : options.skipEmit)) {
        this.emitChange();
      }
    }
  }
  async cacheHandler(locale, componentKey, options) {
    const data = await getComponentTranslations(locale, componentKey);
    if (data == null ? void 0 : data.default) {
      this.updateCache(componentKey, data.default, options);
    }
  }
  getTranslations() {
    if (typeof window === "undefined")
      return {};
    const element = document.getElementById(dictionariesId);
    if (!element)
      return {};
    try {
      return JSON.parse(element.innerHTML);
    } catch (error) {
      console.error(error);
      return {};
    }
  }
  translate(key, options) {
    var _a;
    const locale = this.currentLocale;
    if (!locale) {
      return;
    }
    const libraryByLocale = (_a = this.cache) == null ? void 0 : _a[locale];
    const [componentKey, itemKey] = key.split(".");
    if (libraryByLocale) {
      const component = libraryByLocale[componentKey];
      if (component) {
        return component[itemKey];
      } else {
        this.cacheHandler(locale, componentKey, options);
      }
    } else {
      this.cacheHandler(locale, componentKey, options);
    }
  }
};
function getTranslator() {
  return new Translator();
}

// src/TranslatorProvider.tsx
import React, { useContext, useEffect, useState } from "react";

// src/TranslatorContext.ts
import { createContext } from "react";
var TranslatorContext = createContext({ translator: null });

// src/TranslatorProvider.tsx
var TranslatorProvider = ({ translator: originalTranslator, locale, children }) => {
  const [value, setValue] = useState({
    translator: originalTranslator
  });
  const { translator } = value;
  useState(() => translator.setLocale(locale, { skipEmit: true }));
  useEffect(() => {
    translator.setLocale(locale);
  }, [locale, translator]);
  useEffect(() => {
    const callback = (translator2) => {
      setValue({ translator: translator2 });
    };
    translator.subscribe(callback, { once: true });
    return () => {
      translator.unsubscribe(callback);
    };
  }, [translator]);
  return /* @__PURE__ */ React.createElement(TranslatorContext.Provider, {
    value
  }, children);
};
var useTranslator = () => useContext(TranslatorContext);

// src/useTranslate.ts
import { useEffect as useEffect2, useState as useState2 } from "react";
var useTranslate = (key, options) => {
  const value = useTranslator();
  const isDisabled = !!(options == null ? void 0 : options.disabled);
  const [translation, setTranslation] = useState2(() => {
    var _a;
    return isDisabled ? void 0 : (_a = value.translator) == null ? void 0 : _a.translate(key, { skipEmit: true });
  });
  useEffect2(() => {
    var _a;
    setTranslation(isDisabled ? void 0 : (_a = value.translator) == null ? void 0 : _a.translate(key));
  }, [value, key, isDisabled]);
  return translation;
};
export {
  Translator,
  TranslatorProvider,
  getTranslator,
  useTranslate,
  useTranslator
};
